import React, { useState } from 'react'
import { useIntl } from 'react-intl'

import { Button } from '../../design-system/Button'
import { SegmentedToggle } from '../../design-system/SegmentedToggle'
import { SelectField } from '../../design-system/SelectField'
import type { SelectFieldOption } from '../../design-system/SelectField'
import { Stack } from '../../design-system/Stack'
import { Text } from '../../design-system/Text'
import { TextField } from '../../design-system/TextField'
import calendarHelper from '../months'
import type { LibraryRuleSummary } from './library-rule'
import { HolidayRow, holidayLength, rowYear, utcToday } from './logic'
import {
  DaySet,
  MIN_SPAN_DAYS,
  RecurrenceOption,
  RecurrenceSettings,
  RecurrenceSummary,
  currentRecurrence,
  maxDynamicSpan,
  recurrenceDefaults,
  recurrenceSummary,
  savedRecurrenceSummary,
} from './recurrence'

const dayName = (weekday: number): string => calendarHelper.days.find((day) => day.value === weekday)?.label ?? 'app.sunday'
const monthName = (month: number): string => calendarHelper.months.find((entry) => entry.value === month)?.label ?? 'app.january'

/** The rule in one line: "Every year on March 12", "Every year on the second Thursday of March". */
export function useRecurrenceText(): (summary: RecurrenceSummary) => string {
  const { formatMessage, formatDate } = useIntl()
  return (summary) => {
    if (summary.kind === 'library') return formatMessage({ id: 'holidaysTab.recurrence.summaryLibrary' })
    const day = (date: string) => formatDate(`${date}T00:00:00`, { month: 'long', day: 'numeric' })
    let rule: string
    if (summary.kind === 'dynamic') {
      rule = formatMessage(
        { id: 'holidaysTab.recurrence.summaryDynamic' },
        { week: summary.week, weekday: formatMessage({ id: dayName(summary.weekday) }), month: formatMessage({ id: monthName(summary.month) }) }
      )
    } else if (summary.endDate) {
      rule = formatMessage({ id: 'holidaysTab.recurrence.summaryFixedRange' }, { date: day(summary.date), endDate: day(summary.endDate) })
    } else {
      rule = formatMessage({ id: 'holidaysTab.recurrence.summaryFixed' }, { date: day(summary.date) })
    }
    return summary.transfer ? formatMessage({ id: 'holidaysTab.recurrence.summaryTransfer' }, { rule }) : rule
  }
}

/**
 * What the Repeats yearly cell reads: No, Fixed date, a dynamic rule itself ("Second Thursday
 * of March"), or Library date for an imported holiday, whose date the library sets each year.
 */
export function useRecurrenceValue(): (row: HolidayRow) => string {
  const { formatMessage } = useIntl()
  return (row) => {
    const option = currentRecurrence(row)
    if (option === 'NONE') return formatMessage({ id: 'holidaysTab.recurrence.none' })
    if (row.type === 'LIB') return formatMessage({ id: 'holidaysTab.recurrence.library' })
    const summary = recurrenceSummary(row)
    if (summary.kind !== 'dynamic') return formatMessage({ id: 'holidaysTab.recurrence.fixed' })
    return formatMessage(
      { id: 'holidaysTab.recurrence.dynamicShort' },
      { week: summary.week, weekday: formatMessage({ id: dayName(summary.weekday) }), month: formatMessage({ id: monthName(summary.month) }) }
    )
  }
}

export interface RecurrenceEditorProps {
  row: HolidayRow
  /** The library's rule for an imported row, in place of "Library date". */
  libraryRule?: LibraryRuleSummary
  onCancel: () => void
  /** The rule picked, or null for Not recurring. */
  onSave: (settings: RecurrenceSettings | null) => void
}

// Lists open inside the panel, so picking an option is not a click outside the popover.
const inPanel = (node: HTMLElement): HTMLElement => node.parentElement ?? document.body

// SelectFieldOption has no `disabled` yet; antd's Select reads it.
const disabledOption = (option: SelectFieldOption): SelectFieldOption => ({ ...option, disabled: true }) as SelectFieldOption

/**
 * An imported holiday follows the library: it may stop repeating, but takes no dynamic date and no
 * Transfer, which location-year-holiday-updated does not apply to it.
 */
export function RecurrenceEditor({ row, libraryRule, onCancel, onSave }: RecurrenceEditorProps): React.ReactElement {
  const { formatMessage } = useIntl()
  const toText = useRecurrenceText()
  const [option, setOption] = useState<RecurrenceOption>(() => currentRecurrence(row))
  const [settings, setSettings] = useState<RecurrenceSettings>(() => recurrenceDefaults(row))
  const imported = row.type === 'LIB'
  const multiDay = holidayLength(row) === 'MULTI_DAY'
  const set = (patch: Partial<RecurrenceSettings>) => setSettings((current) => ({ ...current, ...patch }))
  const rule: RecurrenceSettings | null = option === 'NONE' ? null : { ...settings, recurringType: option }

  const toOptions = (entries: { value: number; label: string }[]) => entries.map((entry) => ({ value: entry.value, label: formatMessage({ id: entry.label }) }))

  const dynamicOption = { value: 'SPECIFIC_DAY', label: formatMessage({ id: 'holidaysTab.recurrence.dynamic' }) }
  const options: SelectFieldOption[] = [
    { value: 'NONE', label: formatMessage({ id: 'holidaysTab.recurrence.none' }) },
    { value: 'EXACT_DAY', label: libraryRule?.short ?? formatMessage({ id: imported ? 'holidaysTab.recurrence.library' : 'holidaysTab.recurrence.fixed' }) },
    imported ? disabledOption({ ...dynamicOption, title: formatMessage({ id: 'holidaysTab.recurrence.headerTooltip' }) }) : dynamicOption,
  ]
  const year = rowYear(row)
  // Both are one-way: a custom repeating holiday is locked from then on, and an imported one stops repeating in its own year only.
  // The note also says it is added to the next year, which is only true for this year's rows.
  const note = (() => {
    if (imported && option === 'NONE' && row.recurring) return formatMessage({ id: 'holidaysTab.recurrence.thisYearOnly' }, { year })
    if (imported || option === 'NONE' || row.recurring) return null
    const thisYear = utcToday().year()
    if (year === thisYear) return formatMessage({ id: 'holidaysTab.recurrence.locksAfterSave' }, { nextYear: year + 1 })
    if (year === thisYear + 1) return formatMessage({ id: 'holidaysTab.recurrence.locksAfterSaveShort' })
    return null
  })()

  const daySetPart = (part: 0 | 1 | 2, partOptions: { value: number; label: string }[], labelId: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <SelectField
        ariaLabel={formatMessage({ id: labelId })}
        searchable={false}
        options={partOptions}
        value={settings.daySet[part]}
        getPopupContainer={inPanel}
        onChange={(next) => {
          const daySet = [...settings.daySet] as DaySet
          daySet[part] = Number(next)
          set({ daySet })
        }}
      />
    </div>
  )

  const summary = imported ? (libraryRule?.summary ?? toText(savedRecurrenceSummary(row))) : rule && toText(recurrenceSummary(row, rule))
  const summaryLine = rule && <Text tone="secondary">{summary}</Text>

  return (
    <div
      // Narrower on a phone, so the popover fits inside the viewport
      style={{ width: 'min(360px, calc(100vw - 48px))' }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return
        // Escape on an open select only closes its dropdown, not the whole panel.
        if ((event.target as HTMLElement).getAttribute('aria-expanded') === 'true') {
          event.stopPropagation()
        } else {
          onCancel()
        }
      }}
    >
      <Stack gap="base">
        <Stack gap="xs">
          <SelectField
            label={formatMessage({ id: 'components.createHolidaysForm.recurringType' })}
            searchable={false}
            getPopupContainer={inPanel}
            value={option}
            onChange={(next) => setOption(next as RecurrenceOption)}
            options={options}
          />
          {option === 'EXACT_DAY' && summaryLine}
        </Stack>
        {option === 'SPECIFIC_DAY' && (
          <div role="group" aria-label={formatMessage({ id: 'components.createHolidaysForm.recurringDate' })}>
            <Stack gap="xs">
              <Text>{formatMessage({ id: 'components.createHolidaysForm.recurringDate' })}</Text>
              <Stack direction="horizontal" gap="sm" wrap>
                {daySetPart(0, toOptions(calendarHelper.monthWeeks), 'components.notificationForm.week')}
                {daySetPart(1, toOptions(calendarHelper.days), 'components.notificationForm.day')}
                {daySetPart(2, toOptions(calendarHelper.months), 'components.notificationForm.month')}
              </Stack>
              {summaryLine}
            </Stack>
          </div>
        )}
        {option === 'SPECIFIC_DAY' && multiDay && (
          <TextField
            variant="number"
            label={formatMessage({ id: 'components.tables.duration' })}
            unit={formatMessage({ id: 'app.days' })}
            min={MIN_SPAN_DAYS}
            max={maxDynamicSpan(row, settings.daySet)}
            value={settings.duration}
            onChange={(next) => {
              if (next !== null) {
                set({ duration: Number(next) })
              }
            }}
          />
        )}
        {option !== 'NONE' && !imported && (
          <SegmentedToggle
            label={formatMessage({ id: 'components.createHolidaysForm.transferIfFallsOnNonWorkingDay' })}
            options={[
              { value: true, label: formatMessage({ id: 'app.yes' }) },
              { value: false, label: formatMessage({ id: 'app.no' }) },
            ]}
            value={settings.transfer}
            onChange={(next) => set({ transfer: next === true })}
          />
        )}
        {note && <Text tone="secondary">{note}</Text>}
        <Stack direction="horizontal" gap="sm" justify="flex-end">
          <Button variant="outline" onClick={onCancel}>
            {formatMessage({ id: 'app.cancel' })}
          </Button>
          <Button variant="primary" onClick={() => onSave(rule)}>
            {formatMessage({ id: 'app.save' })}
          </Button>
        </Stack>
      </Stack>
    </div>
  )
}
